import { useMemo, useRef, useState } from 'react';
import { formatDateTime } from '../lib/datetime';
import TopBar from '../components/TopBar';
import { Placeholder } from '../components/Charts';
import {
  NoteDialog, CoachDialog, AnnotateDialog, PatternDialog, CallbackDialog,
} from '../components/triage/TriageModals';
import { kpisService, tasksService } from '../lib/services';
import { useApi } from '../lib/hooks';
import { useToast } from '../lib/toast';
import { apiErrorMessage } from '../lib/apiError';
import { useCustomerOptions, useConsultantOptions } from '../lib/useDirectory';
import {
  parseTranscript, searchMatches, formatClock, callScorecard, scorecardTotals,
  issueLabel, issuePillClass, flagPillClass,
} from '../lib/triage';

const SPEEDS = [0.75, 1, 1.25, 1.5, 2];

export default function TriageCall({ callId, onBack }) {
  const { toast } = useToast();
  const { data: liveCallResp, loading, error, refetch } = useApi(
    () => callId ? kpisService.triageCall(callId) : Promise.resolve(null),
    { fallback: null, deps: [callId], enabled: !!callId },
  );
  const liveCall = liveCallResp?.data ?? liveCallResp;
  const { data: annotations, refetch: refetchNotes } = useApi(
    () => callId ? kpisService.annotations(callId) : Promise.resolve([]),
    { fallback: [], deps: [callId], enabled: !!callId },
  );
  const { options: customerOptions } = useCustomerOptions();
  const { options: userOptions, loading: usersLoading } = useConsultantOptions();

  const [dialog, setDialog] = useState(null);   // 'coach'|'escalate'|'resolve'|'annotate'|'pattern'|'callback'
  const [annotateLine, setAnnotateLine] = useState(null);   // transcript line being annotated, or null = whole call
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);

  // audio
  const audioRef = useRef(null);
  const [time, setTime] = useState({ current: 0, total: 0 });
  const [speedIdx, setSpeedIdx] = useState(1);

  // (Triage.jsx keys this component by call id, so audio/search state resets per call.)
  const lines = useMemo(() => parseTranscript(liveCall?.transcript), [liveCall?.transcript]);
  const matches = useMemo(() => new Set(searchMatches(lines, search)), [lines, search]);
  const notes = useMemo(() => (Array.isArray(annotations) ? annotations : []), [annotations]);
  const notesByLine = useMemo(() => {
    const m = new Map();
    notes.forEach((n) => {
      if (n.lineIndex == null) return;
      m.set(n.lineIndex, [...(m.get(n.lineIndex) || []), n]);
    });
    return m;
  }, [notes]);
  const callNotes = notes.filter((n) => n.lineIndex == null);

  /** Run an action, then refresh so the page reflects it. */
  const act = async (fn, okText, { reloadNotes = false } = {}) => {
    setBusy(true);
    try {
      await fn();
      toast(okText, { variant: 'success' });
      setDialog(null);
      refetch();
      if (reloadNotes) refetchNotes();
    } catch (err) {
      toast(`Couldn’t save: ${apiErrorMessage(err, 'unknown error')}`, { variant: 'danger' });
    } finally {
      setBusy(false);
    }
  };

  if (!callId) {
    return (
      <>
        <TopBar crumbs={['Triage & QA', 'Queue', 'Call Detail']} />
        <div className="empty-state" style={{ padding: '60px 20px' }}>
          <div className="icon">🎙</div>
          <div className="title">Pick a call from the queue</div>
          <div className="desc">Click any row in the Call Queue to see its transcript, scorecard and review actions.</div>
          <button className="btn primary small" style={{ marginTop: 14 }} onClick={onBack}>← Back to queue</button>
        </div>
      </>
    );
  }

  if (!liveCall) {
    return (
      <>
        <TopBar crumbs={['Triage & QA', 'Queue', String(callId).slice(0, 8)]}
                right={<button className="btn small" onClick={onBack}>‹ Back to queue</button>} />
        <div className="empty-state" style={{ padding: '60px 20px' }}>
          {loading ? (
            <div className="title">Loading call…</div>
          ) : (
            <>
              <div className="title">Couldn’t load this call</div>
              <div className="desc">{error ? apiErrorMessage(error, 'unknown error') : 'The call was not found.'}</div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 14 }}>
                <button className="btn small" onClick={refetch}>Retry</button>
                <button className="btn primary small" onClick={onBack}>← Back to queue</button>
              </div>
            </>
          )}
        </div>
      </>
    );
  }

  const customerName = liveCall.customerId
    ? (customerOptions.find((o) => o.id === liveCall.customerId)?.primary || 'Unknown customer')
    : 'Unknown customer';
  const quality = liveCall.qualityScore ?? 0;
  const sentiment = liveCall.sentiment ?? 0;
  const flags = liveCall.flags || [];
  const isCritical = liveCall.status === 'CRITICAL';
  const reviewStatus = liveCall.reviewStatus || 'PENDING_REVIEW';
  const openCoaching = (liveCall.coaching || []).filter((c) => c.status === 'OPEN');
  const scorecard = callScorecard({ sentiment, quality, flags, hasTranscript: lines.length > 0 });
  const { total, max } = scorecardTotals(scorecard);
  const hasRecording = !!liveCall.recordingUrl;

  const seek = (delta) => {
    const a = audioRef.current;
    if (a) a.currentTime = Math.max(0, Math.min(a.duration || Infinity, a.currentTime + delta));
  };
  const cycleSpeed = () => {
    const next = (speedIdx + 1) % SPEEDS.length;
    setSpeedIdx(next);
    if (audioRef.current) audioRef.current.playbackRate = SPEEDS[next];
  };
  const openAnnotate = (line) => { setAnnotateLine(line); setDialog('annotate'); };

  return (
    <>
      <TopBar
        crumbs={['Triage & QA', 'Queue', String(liveCall.callId).slice(0, 8)]}
        right={<button className="btn small" onClick={onBack}>‹ Back to queue</button>}
      />

      {/* Call header */}
      <div style={{ padding: '12px 20px', borderBottom: '1px solid var(--border-color)', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', background: isCritical ? 'var(--danger-bg)' : 'var(--bg-glass)' }}>
        <Placeholder w={42} h={42} round label={customerName.split(' ').map((s) => s[0]).join('').slice(0, 2).toUpperCase()} />
        <div>
          <div style={{ fontSize: 18, fontWeight: 700 }}>{customerName}</div>
          <div className="mono text-xs text-muted">
            {`${liveCall.channel || 'VOICE'} · ${liveCall.direction || ''} · ${formatDateTime(liveCall.timestamp, '')}`}
          </div>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <div style={{ textAlign: 'center' }}><div className="mono text-xs text-muted">QUALITY (EST.)</div><div style={{ fontSize: 26, fontWeight: 800, color: quality < 50 ? 'var(--danger)' : quality < 70 ? 'var(--warning)' : 'var(--success)' }}>{quality}</div></div>
          <div style={{ textAlign: 'center' }}><div className="mono text-xs text-muted">SENTIMENT</div><div style={{ fontSize: 26, fontWeight: 800, color: sentiment < -0.3 ? 'var(--danger)' : sentiment < 0 ? 'var(--warning)' : 'var(--success)' }}>{sentiment.toFixed(2)}</div></div>
          <div style={{ textAlign: 'center' }}><div className="mono text-xs text-muted">FLAGS</div><div style={{ fontSize: 26, fontWeight: 800, color: flags.length > 0 ? 'var(--danger)' : 'var(--text-secondary)' }}>{flags.length}</div></div>
          <div style={{ width: 1, height: 40, background: 'var(--border-color)' }} />
          {reviewStatus !== 'PENDING_REVIEW' && (
            <span className={`pill ${reviewStatus === 'RESOLVED' ? 'success' : 'warning filled'}`}>
              {reviewStatus === 'RESOLVED' ? 'Resolved' : 'Escalated'}{liveCall.reviewedBy ? ` · ${liveCall.reviewedBy}` : ''}
            </span>
          )}
          {openCoaching.length > 0 && (
            <span className="pill accent">Coaching: {openCoaching[0].coachName || 'assigned'}</span>
          )}
          <button className="btn small" disabled={busy} onClick={() => setDialog('coach')}>📋 Assign to coach</button>
          <button className="btn small danger" disabled={busy || reviewStatus === 'ESCALATED'} onClick={() => setDialog('escalate')}>↑ Escalate</button>
          {reviewStatus === 'RESOLVED' ? (
            <button className="btn small" disabled={busy}
                    onClick={() => act(() => kpisService.reopenCall(callId), 'Call re-opened for review')}>↺ Re-open</button>
          ) : (
            <button className="btn small primary" disabled={busy} onClick={() => setDialog('resolve')}>✓ Approve &amp; close</button>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* LEFT — audio + transcript */}
        <div style={{ flex: '1 1 700px', display: 'flex', flexDirection: 'column', borderRight: '1px solid var(--border-color)', minWidth: 0 }}>
          <div style={{ padding: 14, borderBottom: '1px solid var(--border-color)' }}>
            {hasRecording ? (
              <>
                <audio
                  ref={audioRef}
                  src={liveCall.recordingUrl}
                  controls
                  preload="metadata"
                  style={{ width: '100%' }}
                  onTimeUpdate={(e) => setTime({ current: e.target.currentTime, total: e.target.duration || 0 })}
                  onLoadedMetadata={(e) => setTime({ current: e.target.currentTime, total: e.target.duration || 0 })}
                />
                <div className="flex-row" style={{ marginTop: 8, gap: 6, flexWrap: 'wrap' }}>
                  <button className="btn small" onClick={() => seek(-10)}>−10s</button>
                  <button className="btn small" onClick={() => seek(10)}>+10s</button>
                  <button className="btn small" onClick={cycleSpeed}>{SPEEDS[speedIdx]}×</button>
                  <span className="mono text-xs text-muted">{formatClock(time.current)} / {formatClock(time.total)}</span>
                  <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                    <a className="btn small" href={liveCall.recordingUrl} download target="_blank" rel="noreferrer">⬇ Download</a>
                    <button
                      className="btn small"
                      disabled={busy}
                      onClick={() => act(() => kpisService.retranscribe(callId), 'Transcript refreshed')}
                    >↻ Re-transcribe</button>
                  </div>
                </div>
              </>
            ) : (
              <div className="text-muted text-xs">No recording is stored for this call.</div>
            )}
          </div>

          <div className="flex-row" style={{ padding: '8px 14px', borderBottom: '1px solid var(--border-color)', gap: 8 }}>
            <span className="mono text-xs text-muted">TRANSCRIPT · {lines.length} line{lines.length === 1 ? '' : 's'}</span>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
              {searchOpen && (
                <>
                  <input
                    autoFocus
                    value={search}
                    placeholder="Search transcript…"
                    style={{ width: 180 }}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                  <span className="mono text-xs text-muted">{search.trim() ? `${matches.size} match${matches.size === 1 ? '' : 'es'}` : ''}</span>
                </>
              )}
              <button className="btn small" onClick={() => { setSearchOpen((v) => !v); if (searchOpen) setSearch(''); }}>
                🔍 {searchOpen ? 'Close' : 'Search'}
              </button>
              <button className="btn small" onClick={() => openAnnotate(null)}>+ Annotate</button>
            </div>
          </div>

          <div style={{ flex: 1, overflow: 'auto', padding: '12px 14px' }}>
            {lines.length === 0 ? (
              <div className="empty-state" style={{ padding: '40px 8px' }}>
                <div className="title">No transcript available</div>
                <div className="desc">
                  {hasRecording
                    ? 'This call has a recording but no transcript yet — try Re-transcribe.'
                    : 'There is no transcript or recording for this call.'}
                </div>
              </div>
            ) : lines.map((line) => (
              <div
                key={line.index}
                className="transcript-line"
                style={{ marginBottom: 6, background: matches.has(line.index) ? 'var(--accent-glow)' : undefined }}
              >
                <div style={{ flexShrink: 0, width: 72 }}>
                  <div className="mono text-xs" style={{ fontWeight: 700, color: line.speaker === 'AI' ? 'var(--accent-primary-hover)' : 'var(--info)' }}>
                    {line.speaker || `#${line.index + 1}`}
                  </div>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, lineHeight: 1.5 }}>{line.text}</div>
                  {(notesByLine.get(line.index) || []).map((n) => (
                    <div key={n.id} className="annotation-card" style={{ marginTop: 6 }}>
                      <span className={`pill ${issuePillClass(n.tag)}`} style={{ fontSize: 9 }}>{issueLabel(n.tag)}</span>
                      <span className="mono text-xs text-muted">{n.author}</span>
                      {n.note && <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>— {n.note}</span>}
                      <button
                        type="button"
                        className="btn small"
                        style={{ marginLeft: 'auto' }}
                        title="Remove this annotation"
                        onClick={() => act(() => kpisService.deleteAnnotation(n.id), 'Annotation removed', { reloadNotes: true })}
                      >✕</button>
                    </div>
                  ))}
                </div>
                <button type="button" className="btn small" title="Annotate this line" onClick={() => openAnnotate(line)}>✎</button>
              </div>
            ))}
          </div>
        </div>

        {/* RIGHT — insights */}
        <div style={{ width: 440, display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
          <div style={{ flex: 1, overflow: 'auto', padding: 14 }}>
            {(reviewStatus !== 'PENDING_REVIEW' || liveCall.reviewNote || liveCall.flagged) && (
              <div className="card" style={{ marginBottom: 12 }}>
                <div className="card-label">REVIEW</div>
                <div style={{ fontSize: 13 }}>
                  {reviewStatus === 'RESOLVED' ? 'Resolved' : reviewStatus === 'ESCALATED' ? 'Escalated' : 'Pending review'}
                  {liveCall.reviewedBy ? ` by ${liveCall.reviewedBy}` : ''}
                  {liveCall.reviewedAt ? ` · ${formatDateTime(liveCall.reviewedAt, '')}` : ''}
                </div>
                {liveCall.flagged && (
                  <div className="text-xs" style={{ marginTop: 4 }}>
                    ⚑ Flagged{liveCall.flagReason ? `: ${liveCall.flagReason}` : ''}
                  </div>
                )}
                {liveCall.reviewNote && <div className="text-xs text-muted" style={{ marginTop: 4 }}>“{liveCall.reviewNote}”</div>}
              </div>
            )}

            {(liveCall.coaching || []).length > 0 && (
              <div className="card" style={{ marginBottom: 12 }}>
                <div className="card-label">COACHING</div>
                {liveCall.coaching.map((c) => (
                  <div key={c.id} className="flex-between" style={{ padding: '4px 0', fontSize: 12, gap: 8 }}>
                    <span>
                      {c.coachName || 'Coach'}{c.dueDate ? ` · due ${c.dueDate}` : ''}
                      {c.note ? <span className="text-muted"> — {c.note}</span> : null}
                    </span>
                    <span className={`pill ${c.status === 'DONE' ? 'success' : 'accent'}`}>{c.status === 'DONE' ? 'Done' : 'Open'}</span>
                  </div>
                ))}
                {openCoaching.length > 0 && (
                  <button className="btn small" style={{ marginTop: 8 }} disabled={busy}
                          onClick={() => act(() => kpisService.completeCoaching(callId), 'Coaching marked as done')}>
                    ✓ Mark coaching done
                  </button>
                )}
              </div>
            )}

            <div className="card" style={{ marginBottom: 12 }}>
              <div className="card-label">QUALITY SCORECARD · {total}/{max} · ESTIMATED</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {scorecard.map(([k, v, m, type, note]) => (
                  <div key={k}>
                    <div className="flex-between" style={{ fontSize: 12 }}>
                      <span>{k}</span>
                      <span className={`mono text-xs text-${type}`} style={{ fontWeight: 700 }}>{v}/{m}</span>
                    </div>
                    <div className="progress-bar" style={{ marginTop: 3 }}>
                      <div className={`fill ${type}`} style={{ width: `${(v / m) * 100}%` }} />
                    </div>
                    {note && <div className={`mono text-xs text-${type}`} style={{ marginTop: 2 }}>{note}</div>}
                  </div>
                ))}
              </div>
              <div className="mono text-xs text-muted" style={{ marginTop: 8 }}>
                Derived from call sentiment and flags — there is no per-criterion call analysis yet.
              </div>
            </div>

            <div className="card" style={{ marginBottom: 12 }}>
              <div className="card-label">FLAGS RAISED</div>
              {flags.length === 0 ? (
                <div className="mono text-xs text-muted">No flags raised on this call.</div>
              ) : flags.map((f) => (
                <div key={f} className="flex-between" style={{ padding: '4px 0', fontSize: 12 }}>
                  <span>{String(f)}</span>
                  <span className={`pill ${flagPillClass(f)}`} style={{ fontSize: 9 }}>FLAGGED</span>
                </div>
              ))}
              <button
                className="btn small"
                style={{ marginTop: 8 }}
                disabled={busy}
                onClick={() => act(
                  () => kpisService.flagCall(callId, { flagged: !liveCall.flagged, reason: liveCall.flagged ? null : 'Flagged from call detail' }),
                  liveCall.flagged ? 'Flag removed' : 'Call flagged',
                )}
              >
                {liveCall.flagged ? '⚑ Remove manual flag' : '⚑ Flag this call'}
              </button>
            </div>

            {callNotes.length > 0 && (
              <div className="card" style={{ marginBottom: 12 }}>
                <div className="card-label">NOTES ON THIS CALL</div>
                {callNotes.map((n) => (
                  <div key={n.id} className="annotation-card" style={{ marginBottom: 6 }}>
                    <span className={`pill ${issuePillClass(n.tag)}`} style={{ fontSize: 9 }}>{issueLabel(n.tag)}</span>
                    <span className="mono text-xs text-muted">{n.author}</span>
                    {n.note && <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>— {n.note}</span>}
                    <button
                      type="button" className="btn small" style={{ marginLeft: 'auto' }} title="Remove this note"
                      onClick={() => act(() => kpisService.deleteAnnotation(n.id), 'Note removed', { reloadNotes: true })}
                    >✕</button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div style={{ padding: 12, borderTop: '1px solid var(--border-color)', background: 'var(--bg-glass)' }}>
            <div className="mono text-xs text-muted" style={{ marginBottom: 6 }}>TAKE ACTION TO IMPROVE</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <button className="btn small" onClick={() => setDialog('pattern')}>📚 Add to coaching library</button>
              <button
                className="btn small"
                disabled={!liveCall.customerId && !liveCall.leadId}
                title={!liveCall.customerId && !liveCall.leadId ? 'This call is not linked to a customer' : undefined}
                onClick={() => setDialog('callback')}
              >📞 Schedule human callback to {customerName}</button>
            </div>
          </div>
        </div>
      </div>

      {dialog === 'coach' && (
        <CoachDialog
          title="Assign this call to a coach"
          options={userOptions}
          loading={usersLoading}
          busy={busy}
          onClose={() => setDialog(null)}
          onConfirm={(v) => act(() => kpisService.assignCoaching(callId, v), 'Assigned to coach')}
        />
      )}
      {dialog === 'escalate' && (
        <NoteDialog
          title="Escalate this call"
          intro="Marks the call as escalated so a manager can decide what happens next."
          noteLabel="Why is it being escalated? (optional)"
          confirmLabel="Escalate"
          busy={busy}
          onClose={() => setDialog(null)}
          onConfirm={(note) => act(() => kpisService.escalateCall(callId, { note }), 'Call escalated')}
        />
      )}
      {dialog === 'resolve' && (
        <NoteDialog
          title="Approve & close this call"
          intro="Marks the call as resolved. It leaves the Flagged and Critical lists."
          confirmLabel="Approve & close"
          busy={busy}
          onClose={() => setDialog(null)}
          onConfirm={(note) => act(() => kpisService.resolveCall(callId, { note }), 'Call approved and closed')}
        />
      )}
      {dialog === 'annotate' && (
        <AnnotateDialog
          lineText={annotateLine?.text}
          busy={busy}
          onClose={() => setDialog(null)}
          onConfirm={({ tag, note }) => act(
            () => kpisService.annotate(callId, { tag, note, lineIndex: annotateLine ? annotateLine.index : null }),
            'Annotation saved', { reloadNotes: true })}
        />
      )}
      {dialog === 'pattern' && (
        <PatternDialog
          busy={busy}
          onClose={() => setDialog(null)}
          onConfirm={(v) => act(() => kpisService.createPattern({ ...v, sourceCallId: callId }), 'Added to the coaching library')}
        />
      )}
      {dialog === 'callback' && (
        <CallbackDialog
          customerLabel={customerName}
          options={userOptions}
          loading={usersLoading}
          busy={busy}
          onClose={() => setDialog(null)}
          onConfirm={({ assigneeId, when, note }) => act(() => tasksService.create({
            leadId: liveCall.leadId || null,
            customerId: liveCall.customerId || null,
            assigneeId,
            type: 'CALL',
            title: `Call back ${customerName} (triage review)`,
            notes: note || `Follow-up from call ${String(callId).slice(0, 8)} reviewed in Triage & QA.`,
            dueAt: new Date(when).toISOString(),
            priority: 'HIGH',
          }), 'Callback task created')}
        />
      )}
    </>
  );
}
